import { useRouter } from 'next/router'
import { useDispatch } from 'react-redux'
import { useState, useEffect } from 'react'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'

import { setPreselectVender, setNavigateInfo } from 'store/cache/cacheSlice'
import { genPathByNavData } from 'utils/api'
import { GtmAgent } from 'utils/hooks/analytics'
import NowrapDiv from 'components/common/NowrapDiv'
import ButtonBasic from 'components/common/ButtonBasic'
import { FunctionModal, ScrollableBox } from 'components/common/Modal'
import styled from '@emotion/styled'
import { useVenderOrderService } from 'utils/api/hooks'

const StyleWrapper = styled(Box)`
  ${({ theme }) => `
    position: relative;
    background-color: ${theme.palette.background.paper};
    width: 100%;
    height: 100%;
    margin: 0 auto;

    ${theme.breakpoints.up('lg')} {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      margin: 0;
      width: 724px;
      height: 444px;
      border-radius: 10px;
    }
  `}
`

const Controls = ({
  vender,
  $height,
  isMobileByWidth,
  onExternalLinkClick,
}) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const [serviceModal, setServiceModal] = useState({ open: false })
  const [selectedService, setSelectedService] = useState(null)

  const { serviceItem } = router.query
  const { data: supportServicesData } = useVenderOrderService({
    venderId: vender?._id,
  })

  const serviceOptions =
    supportServicesData?.orderServices?.map((service) => ({
      serviceId: service._id,
      serviceName: service.name,
      identityCode: service.identityCode,
    })) || []

  // venderServiceItems 廠商介紹資訊的服務項目，會大於等於 serviceOptions = 有效派案與廠商介紹資訊的服務項目的交集
  // 等於的話，相當於沒有額外的服務項目
  const isHintVisible =
    serviceOptions.length < vender?.venderServiceItems.length

  // 預選服務
  useEffect(() => {
    if (serviceItem && serviceOptions.length > 1 && !selectedService) {
      const preSelectedService = serviceOptions.find(
        (service) => service.serviceId === serviceItem
      )

      if (preSelectedService) {
        setSelectedService(preSelectedService)
      }
    }
  }, [serviceItem, serviceOptions])

  const handleReservation = async (service) => {
    dispatch(setNavigateInfo({ id: vender._id, type: 'Vender' }))
    if (!!vender?.navData?.page) {
      if (vender?.navData?.action === 'venderProvideServiceJudgment') {
        dispatch(setPreselectVender(vender._id))
        router.push(`/service/${service?.identityCode}/booking`)
      } else {
        GtmAgent.directSendToGTM({
          event: 'living_action',
          living_event_name: 'booking_to_link',
          living_vender: vender?.name,
          living_vender_id: vender?._id,
          living_click_name: vender?.gotoReservationDefaultName || '點我前往',
          living_click_location: '廠商頁',
        })

        const { path, isExternalLink } = await genPathByNavData(vender.navData)
        if (!path) return

        // 有連結則確認是否為居家 domain
        if (isExternalLink) {
          if (onExternalLinkClick) {
            const buttonName =
              vender.gotoReservationDefaultName || '透過信義居家預約'
            onExternalLinkClick(path, buttonName)
          } else {
            window.open(path, '_blank')
          }
          return
        }

        if (/^\/service\//.test(path)) {
          dispatch(setPreselectVender(vender._id))
        }
        router.push(path)
      }
    } else {
      router.push('/')
    }
  }

  const openServiceModal = () => setServiceModal({ open: true })

  const closeServiceModal = () => setServiceModal({ open: false })

  const handleConfirm = () => {
    if (selectedService) {
      handleReservation(selectedService)
      GtmAgent.directSendToGTM({
        event: 'living_action',
        living_event_name: 'booking_service',
        living_vender_id: vender?._id,
        living_click_name:
          vender?.gotoReservationDefaultName || '透過信義居家預約',
        living_click_location: '廠商頁彈窗-預約服務',
        living_vender_service: selectedService.serviceName,
        living_vender: vender?.name,
      })
      closeServiceModal()
    }
  }

  const handleServiceChange = (event) => {
    const serviceObject = serviceOptions.find(
      (option) => option.serviceId === event.target.value
    )
    setSelectedService(serviceObject)
  }

  const handleButtonClick = () => {
    if (vender?.navData?.action === 'venderProvideServiceJudgment') {
      if (serviceOptions.length === 1) {
        handleReservation(serviceOptions[0])
        GtmAgent.directSendToGTM({
          event: 'living_action',
          living_event_name: 'booking_service',
          living_vender_id: vender?._id,
          living_click_name:
            vender?.gotoReservationDefaultName || '透過信義居家預約',
          living_click_location: '廠商頁',
          living_vender_service: serviceOptions[0].serviceName,
          living_vender: vender?.name,
        })
      } else {
        openServiceModal()
      }
    } else {
      handleReservation()
    }
  }

  // 只有當有服務選項時才顯示按鈕
  if (
    vender?.navData?.action === 'venderProvideServiceJudgment' &&
    !serviceOptions.length
  ) {
    return null
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Button
            id="book"
            className="action"
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleButtonClick}
            sx={{
              fontSize: '18px',
              height: $height ? `${$height}` : 'auto',
            }}
          >
            <NowrapDiv>
              {vender.gotoReservationDefaultName || '透過信義居家預約'}
            </NowrapDiv>
          </Button>
        </Grid>
      </Grid>

      <FunctionModal
        open={serviceModal.open}
        onClose={closeServiceModal}
        ModalWrapper={StyleWrapper}
      >
        <Box display="flex" flexDirection="column" height="100%" p={5} gap={5}>
          <Box
            display="flex"
            justifyContent={isMobileByWidth ? 'center' : 'flex-start'}
            position="relative"
            pb={isMobileByWidth ? 5 : 0}
          >
            <Typography variant="titleLarge" color="text.minus1">
              預約服務
            </Typography>
          </Box>
          {isHintVisible && (
            <Typography variant="paraBodyMedium" color="text.minus1">
              *部分服務項目未提供線上預約功能，因此未顯示於此，需自行聯絡廠商
            </Typography>
          )}

          <ScrollableBox flexGrow={1}>
            <RadioGroup
              value={selectedService?.serviceId || ''}
              onChange={handleServiceChange}
            >
              <Grid container columnSpacing={isMobileByWidth ? 0 : 5}>
                {serviceOptions.map((option) => (
                  <Grid
                    key={option.serviceId}
                    item
                    xs={isMobileByWidth ? 12 : 6}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <FormControlLabel
                      value={option.serviceId}
                      control={<Radio sx={{ padding: 0 }} />}
                      label={
                        <Typography
                          variant="paraBodyMedium"
                          color="text.minus2"
                          marginLeft={2.5}
                        >
                          {option.serviceName}
                        </Typography>
                      }
                      sx={{
                        width: '100%',
                        padding: '10px 15px',
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
          </ScrollableBox>

          {isMobileByWidth ? (
            <Grid container spacing={2.5}>
              <Grid item xs={6}>
                <ButtonBasic
                  variant="outlined"
                  fullWidth
                  onClick={closeServiceModal}
                  size="large"
                  color="neutral"
                >
                  關閉
                </ButtonBasic>
              </Grid>
              <Grid item xs={6}>
                <ButtonBasic
                  variant="contained"
                  fullWidth
                  onClick={handleConfirm}
                  disabled={!selectedService}
                  size="large"
                  color="primary"
                >
                  確認
                </ButtonBasic>
              </Grid>
            </Grid>
          ) : (
            <Box display="flex" justifyContent="flex-end" gap={2.5}>
              <Box>
                <ButtonBasic
                  variant="outlined"
                  onClick={closeServiceModal}
                  size="medium"
                  color="neutral"
                >
                  關閉
                </ButtonBasic>
              </Box>
              <ButtonBasic
                variant="contained"
                onClick={handleConfirm}
                disabled={!selectedService}
                size="medium"
                color="primary"
              >
                確認
              </ButtonBasic>
            </Box>
          )}
        </Box>
      </FunctionModal>
    </>
  )
}

export default Controls
